import { useEffect, useMemo, useRef, useState } from 'react'
import { getThreeJsRendererMethods } from 'renderer/viewer/three/threeJsMethods'
import { appQueryParams } from '../appParams'
import { REPLAY_FX_EVENT, emitReplayFxEvent, type ReplayFxEventDetail } from '../replayFx'
import { resolveReplayFxCue, type ReplayFxFlashTone, type ReplayFxMode } from './replayFxCues'
import { REPLAY_FX_MODE_LAYERS } from './replayFxLayers'
import {
  DEFAULT_REPLAY_FX_BEAT_MS,
  REPLAY_FX_CUE_DEFINITIONS,
  REPLAY_FX_TIMELINE_LENGTH_BEATS,
  type ReplayFxTimelineCueDefinition,
} from './replayFxTimeline'
import './ReplayFxOverlay.css'

type ReplayFxFlash = {
  id: number
  left: number
  width: number
  delayMs: number
  durationMs: number
  tone: ReplayFxFlashTone
  variant: 'bolt' | 'column' | 'shard'
  tilt: number
  intensity: number
}

const SHOWCASE_HOLD_BEATS = 6
const SHOWCASE_SEQUENCE = REPLAY_FX_CUE_DEFINITIONS

// From the first play press onward the sky tear lives its own life: it keeps
// cycling on wall-clock time through pause, seeking, even past the replay end —
// the illusion layer sits outside the recording's timeline. Only loading a new
// file silences it.
const SKY_PORTAL_CYCLE_PERIOD_MS = 11_000
const SKY_PORTAL_CYCLE_VISIBLE_MS = 7000
// DEV PREVIEW OFF — the portal was cycling continuously from page load,
// burning GPU even when idle. Now it only activates on real lifecycle cues
// (playback-start). This frees the compositor during chunk loading.
const SKY_PORTAL_DEV_PREVIEW = false


type ReplayFxFlashRecipe = {
  count: number
  tone: ReplayFxFlashTone
  durationScale: number
}

const createFlash = (
  id: number,
  index: number,
  recipe: ReplayFxFlashRecipe
): ReplayFxFlash => {
  const { count, tone, durationScale } = recipe
  const variants: Array<ReplayFxFlash['variant']> = ['bolt', 'column', 'shard']
  const variant = variants[(id + index) % variants.length]!
  const baseLeft = count <= 1 ? 50 : 12 + (index * (76 / Math.max(1, count - 1)))
  const jitter = Math.sin((id + 1) * 12.9898) * 5.5
  const baseWidth = variant === 'column' ? 1.2 : variant === 'shard' ? 5.4 : 4.6
  return {
    id,
    left: Math.max(3, Math.min(96, baseLeft + jitter)),
    width: baseWidth + ((id % 3) * 0.9),
    delayMs: index * 92,
    durationMs: Math.round((390 + ((id % 4) * 52)) * durationScale),
    tone,
    variant,
    tilt: -9 + ((id * 7) % 19),
    intensity: 0.82 + ((id % 4) * 0.08),
  }
}

export default function ReplayFxOverlay () {
  const masterDisabled = appQueryParams.replayFx === '0'
  const soloLayer = appQueryParams.replayFxSolo || ''
  const cssDisabled = masterDisabled || appQueryParams.replayFxCss === '0'
  const portalDisabled = masterDisabled || appQueryParams.replayFxPortal === '0'
  const disabled = masterDisabled
  const showcaseFromQuery = ['1', 'true'].includes(appQueryParams.replayFxShowcase ?? '')
  const showcaseHold = ['1', 'true'].includes(appQueryParams.replayFxShowcaseHold ?? '')
  const requestedShowcaseTempo = Number(appQueryParams.replayFxShowcaseTempo ?? DEFAULT_REPLAY_FX_BEAT_MS)
  const showcaseBeatMs = Number.isFinite(requestedShowcaseTempo)
    ? Math.max(600, Math.min(2400, requestedShowcaseTempo))
    : DEFAULT_REPLAY_FX_BEAT_MS
  const requestedShowcaseStage = String(appQueryParams.replayFxShowcaseStage ?? '')
  const requestedShowcaseStageIndex = SHOWCASE_SEQUENCE.findIndex(stage => stage.id === requestedShowcaseStage)
  const showcaseStartStageIndex = requestedShowcaseStageIndex >= 0 ? requestedShowcaseStageIndex : 0
  const requestedShowcaseBeat = Number(appQueryParams.replayFxShowcaseBeat ?? 0)
  const showcaseStartBeat = requestedShowcaseStageIndex >= 0
    ? SHOWCASE_SEQUENCE[showcaseStartStageIndex]!.startBeat
    : Number.isFinite(requestedShowcaseBeat)
      ? Math.max(0, Math.min(REPLAY_FX_TIMELINE_LENGTH_BEATS - 1, requestedShowcaseBeat))
      : 0
  const [mode, setMode] = useState<ReplayFxMode>('idle')
  // displayedMode lags ~160ms behind `mode` when going to 'idle', so the
  // overlay root's opacity transition (140ms) can fade the layers out before
  // they unmount. Going active is instant.
  const [displayedMode, setDisplayedMode] = useState<ReplayFxMode>('idle')
  const [flashes, setFlashes] = useState<ReplayFxFlash[]>([])
  const [frozen, setFrozen] = useState(false)
  const [showcaseActive, setShowcaseActive] = useState(showcaseFromQuery)
  const idRef = useRef(0)
  const launchPlayedRef = useRef(false)
  const portalCycleTimerRef = useRef<ReturnType<typeof setInterval>>()
  const showcaseStageRef = useRef(showcaseStartStageIndex)
  const cueTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([])
  const showcaseTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([])
  const showcaseActiveRef = useRef(showcaseFromQuery)
  const visible = mode !== 'idle' && !cssDisabled

  // F key = freeze/unfreeze all FX animations for screenshots
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key.toLowerCase() === 'f' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        setFrozen(v => !v)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // Lag the displayed mode so layers survive the 140ms root opacity fade-out.
  useEffect(() => {
    if (mode === 'idle') {
      const timer = setTimeout(() => setDisplayedMode('idle'), 160)
      return () => clearTimeout(timer)
    }
    setDisplayedMode(mode)
  }, [mode])

  const clearCueTimers = () => {
    for (const timer of cueTimersRef.current) {
      clearTimeout(timer)
    }
    cueTimersRef.current = []
  }

  const clearShowcaseTimers = () => {
    for (const timer of showcaseTimersRef.current) {
      clearTimeout(timer)
    }
    showcaseTimersRef.current = []
  }

  const scheduleCue = (handler: () => void, delayMs: number) => {
    const timer = setTimeout(handler, delayMs)
    cueTimersRef.current.push(timer)
  }

  const scheduleShowcase = (handler: () => void, delayMs: number) => {
    const timer = setTimeout(handler, delayMs)
    showcaseTimersRef.current.push(timer)
  }

  const triggerFlashes = (count: number, tone: ReplayFxFlashTone = 'cold') => {
    const durationScale = showcaseActiveRef.current
      ? Math.max(2.6, showcaseBeatMs / 280)
      : 1
    const next = Array.from(
      { length: count },
      (_, index) => createFlash(++idRef.current, index, { count, tone, durationScale })
    )
    setFlashes(prev => [...prev.slice(-10), ...next])
    const removeAfterMs = Math.max(...next.map(flash => flash.delayMs + flash.durationMs)) + 220
    scheduleCue(() => {
      const ids = new Set(next.map(flash => flash.id))
      setFlashes(prev => prev.filter(flash => !ids.has(flash.id)))
    }, removeAfterMs)
  }

  useEffect(() => {
    if (disabled) return

    const triggerPortal = (ms: number) => {
      const debugPortalOff = document.documentElement.classList.contains('replayFxLayerOff-portal')
      if ((portalDisabled || debugPortalOff) && ms > 0) return
      getThreeJsRendererMethods()?.triggerSkyPortal(ms)
    }

    const applyReplayFxEvent = (detail: ReplayFxEventDetail) => {
      if (!detail?.type) return

      const cue = resolveReplayFxCue(detail)
      if (cue.interrupt) {
        clearCueTimers()
      }
      for (const cueStep of cue.steps) {
        const runStep = () => {
          if (cueStep.clearFlashes) {
            setFlashes([])
          }
          if (cueStep.mode) {
            setMode(cueStep.mode)
          }
          if (cueStep.flashes) {
            triggerFlashes(cueStep.flashes.count, cueStep.flashes.tone)
          }
          if (typeof cueStep.skyPortalMs === 'number') {
            triggerPortal(cueStep.skyPortalMs)
          }
        }
        if (cueStep.delayMs === 0) {
          runStep()
        } else {
          scheduleCue(runStep, cueStep.delayMs)
        }
      }
    }

    const startPortalCycle = () => {
      if (portalCycleTimerRef.current) return
      if (portalDisabled) return
      const cycleTick = () => {
        if (showcaseActiveRef.current) return
        triggerPortal(SKY_PORTAL_CYCLE_VISIBLE_MS)
      }
      cycleTick()
      portalCycleTimerRef.current = setInterval(cycleTick, SKY_PORTAL_CYCLE_PERIOD_MS)
    }

    const stopPortalCycle = () => {
      if (!portalCycleTimerRef.current) return
      clearInterval(portalCycleTimerRef.current)
      portalCycleTimerRef.current = undefined
    }

    const handleReplayFxEvent = (detail: ReplayFxEventDetail) => {
      if (showcaseActiveRef.current) return
      let resolvedDetail = detail
      if (detail.type === 'playback-start') {
        // the eye-blink launch scene plays once per loaded file; every later
        // play press is just a jolt (see the repeat cue)
        if (launchPlayedRef.current) {
          resolvedDetail = { ...detail, repeat: true }
        }
        launchPlayedRef.current = true
        startPortalCycle()
      } else if (detail.type === 'file-loading' || detail.type === 'clear') {
        launchPlayedRef.current = false
        stopPortalCycle()
      }
      applyReplayFxEvent(resolvedDetail)
    }

    const getShowcaseDetail = (stage: ReplayFxTimelineCueDefinition): ReplayFxEventDetail => ({
      ...stage.detail,
      durationMs: stage.durationBeats * showcaseBeatMs,
      at: performance.now(),
    })

    const showShowcaseStage = (stageIndex: number) => {
      const normalizedIndex = (stageIndex + SHOWCASE_SEQUENCE.length) % SHOWCASE_SEQUENCE.length
      showcaseStageRef.current = normalizedIndex
      clearCueTimers()
      applyReplayFxEvent(getShowcaseDetail(SHOWCASE_SEQUENCE[normalizedIndex]!))
    }

    const stopShowcase = (clear = true) => {
      showcaseActiveRef.current = false
      setShowcaseActive(false)
      clearShowcaseTimers()
      if (clear) {
        applyReplayFxEvent({ type: 'clear', at: performance.now() })
      }
    }

    const runShowcaseCycle = () => {
      if (!showcaseActiveRef.current) return
      if (showcaseHold) {
        showShowcaseStage(showcaseStageRef.current)
        scheduleShowcase(runShowcaseCycle, SHOWCASE_HOLD_BEATS * showcaseBeatMs)
        return
      }
      for (const showcaseCue of SHOWCASE_SEQUENCE) {
        const relativeBeat = (
          showcaseCue.startBeat -
          showcaseStartBeat +
          REPLAY_FX_TIMELINE_LENGTH_BEATS
        ) % REPLAY_FX_TIMELINE_LENGTH_BEATS
        scheduleShowcase(() => {
          if (!showcaseActiveRef.current) return
          showcaseStageRef.current = SHOWCASE_SEQUENCE.indexOf(showcaseCue)
          applyReplayFxEvent(getShowcaseDetail(showcaseCue))
        }, relativeBeat * showcaseBeatMs)
      }
      scheduleShowcase(runShowcaseCycle, REPLAY_FX_TIMELINE_LENGTH_BEATS * showcaseBeatMs)
    }

    const startShowcase = () => {
      clearCueTimers()
      clearShowcaseTimers()
      showcaseStageRef.current = showcaseStartStageIndex
      showcaseActiveRef.current = true
      setShowcaseActive(true)
      runShowcaseCycle()
    }

    let removeListener = () => {}
    if (typeof customEvents === 'undefined') {
      const handleDomReplayFxEvent = (event: Event) => {
        handleReplayFxEvent((event as CustomEvent<ReplayFxEventDetail>).detail)
      }
      document.addEventListener(REPLAY_FX_EVENT, handleDomReplayFxEvent)
      removeListener = () => {
        document.removeEventListener(REPLAY_FX_EVENT, handleDomReplayFxEvent)
      }
    } else {
      ;(customEvents as any).on('replayFx', handleReplayFxEvent)
      removeListener = () => {
        const events = customEvents as any
        if (typeof events.off === 'function') {
          events.off('replayFx', handleReplayFxEvent)
        } else {
          events.removeListener('replayFx', handleReplayFxEvent)
        }
      }
    }
    const replayFxApi = {
      emit: emitReplayFxEvent,
      startShowcase,
      stopShowcase,
      nextShowcase: () => showShowcaseStage(showcaseStageRef.current + 1),
      previousShowcase: () => showShowcaseStage(showcaseStageRef.current - 1),
      showShowcase (stage: number | string) {
        const stageIndex = typeof stage === 'number'
          ? stage
          : SHOWCASE_SEQUENCE.findIndex(entry => entry.id === stage)
        if (stageIndex < 0) return false
        clearShowcaseTimers()
        showcaseActiveRef.current = true
        setShowcaseActive(true)
        showShowcaseStage(stageIndex)
        return true
      },
    }
    globalThis.__replayFx = replayFxApi
    if (showcaseFromQuery) {
      startShowcase()
    } else if (SKY_PORTAL_DEV_PREVIEW) {
      startPortalCycle()
      scheduleCue(() => {
        if (!showcaseActiveRef.current) {
          triggerPortal(SKY_PORTAL_CYCLE_VISIBLE_MS)
        }
      }, 900)
    }
    return () => {
      removeListener()
      clearCueTimers()
      clearShowcaseTimers()
      stopPortalCycle()
      getThreeJsRendererMethods()?.triggerSkyPortal(0)
      if (globalThis.__replayFx === replayFxApi) {
        globalThis.__replayFx = undefined
      }
    }
  }, [
    disabled,
    portalDisabled,
    showcaseBeatMs,
    showcaseFromQuery,
    showcaseHold,
    showcaseStartBeat,
    showcaseStartStageIndex,
  ])

  const className = useMemo(() => {
    return [
      'replayFxOverlay',
      visible ? 'replayFxOverlay--visible' : '',
      showcaseActive ? 'replayFxOverlay--showcase' : '',
      `replayFxOverlay--${displayedMode}`,
      soloLayer ? `replayFxOverlay--solo-${soloLayer}` : '',
      frozen ? 'replayFxOverlay--frozen' : '',
    ].filter(Boolean).join(' ')
  }, [displayedMode, showcaseActive, visible])

  // When the CSS overlay is disabled (`replayFxCss=0` or master `replayFx=0`)
  // there is nothing to render — the portal side keeps running via the effect
  // above. Returning null here does NOT unmount the effect, so portal triggers
  // still fire when `replayFxPortal` is not also disabled.
  if (cssDisabled) return null

  const layers = REPLAY_FX_MODE_LAYERS[displayedMode] ?? new Set()

  return (
    <div
      className={className}
      aria-hidden="true"
      style={{
        '--replay-fx-beat': `${showcaseBeatMs}ms`,
      } as React.CSSProperties}
    >
      {layers.has('backdrop') && <div className="replayFxOverlay__backdrop" />}
      {layers.has('shutters') && <div className="replayFxOverlay__shutters" />}
      {layers.has('tube') && <div className="replayFxOverlay__tube" />}
      {layers.has('phosphor') && <div className="replayFxOverlay__phosphor" />}
      {layers.has('static') && <div className="replayFxOverlay__static" />}
      {layers.has('scanlines') && <div className="replayFxOverlay__scanlines" />}
      {layers.has('dataRails') && <div className="replayFxOverlay__dataRails" />}
      {layers.has('electricField') && <div className="replayFxOverlay__electricField" />}
      {layers.has('techFrame') && <div className="replayFxOverlay__techFrame" />}
      {layers.has('chromatic') && <div className="replayFxOverlay__chromatic" />}
      {layers.has('horizon') && <div className="replayFxOverlay__horizon" />}
      {layers.has('vignette') && <div className="replayFxOverlay__vignette" />}
      {flashes.map(flash => {
        const style = {
          '--replay-fx-left': `${flash.left}%`,
          '--replay-fx-width': `${flash.width}vw`,
          '--replay-fx-delay': `${flash.delayMs}ms`,
          '--replay-fx-duration': `${flash.durationMs}ms`,
          '--replay-fx-tilt': `${flash.tilt}deg`,
          '--replay-fx-intensity': flash.intensity,
        } as React.CSSProperties
        return (
          <div key={flash.id}>
            <div
              className={[
                'replayFxOverlay__flashWash',
                `replayFxOverlay__flashWash--${flash.tone}`,
              ].join(' ')}
              style={style}
            />
            <div
              className={[
                'replayFxOverlay__flash',
                `replayFxOverlay__flash--${flash.tone}`,
                `replayFxOverlay__flash--${flash.variant}`,
              ].join(' ')}
              style={style}
            >
              <span className="replayFxOverlay__flashCore" />
              <span className="replayFxOverlay__flashEcho" />
            </div>
          </div>
        )
      })}
    </div>
  )
}
